import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalController, PickerController, AlertController } from '@ionic/angular';
import { forkJoin } from 'rxjs';

import { Capacitor } from '@capacitor/core';

import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { Contact } from '../models/contact';
import { Invoice } from '../models/invoice';
import { Quote } from '../models/quote';
import { WorkItem } from '../models/work-item';
import { Business } from '../models/business';

import { ApiService } from '../services/api.service';
import { AuthService } from '../services/auth.service';
import { AiService } from '../services/ai.service';

import { Router } from '@angular/router';

import { ContactsSelectorComponent } from '../contacts-selector/contacts-selector.component';
import { BusinessSelectorComponent } from '../business-selector/business-selector.component';
import { WorkItemChooserComponent } from '../work-item-chooser/work-item-chooser.component';
import { WorkItemEditComponent } from '../work-item-edit/work-item-edit.component';
import { PropertyChooserComponent } from '../property-chooser/property-chooser.component';

import { Property } from '../models/property';

@Component({
  selector: 'app-quote-new',
  templateUrl: './quote-new.page.html',
  styleUrls: ['./quote-new.page.scss']
})
export class QuoteNewPage implements OnInit {

  chosenContact: Contact;
  chosenBusiness: Business;
  isNativePlatform = true;
  ionicForm: UntypedFormGroup;
  isSubmitted = false;
  properties = [];
  WorkItems: WorkItem[];
  newQuote: Quote;
  accountCurrency: string;
  loadingFromAI: boolean = false;
  @Input() public contactId: number;
  @Input() public jobId: number;

  constructor(
    public modalCtrl: ModalController, 
    public pickerCtrl: PickerController,
    public alertController: AlertController, 
    public formBuilder: UntypedFormBuilder, 
    public apiService: ApiService, 
    public router: Router,
    public authService: AuthService,
    public aiService: AiService) { }

  ngOnInit() {
    this.isNativePlatform = Capacitor.isNativePlatform();

    var todaysDate = new Date();

    // Build Quote Form
    this.ionicForm = this.formBuilder.group({
      contactId: ['', [Validators.required]],
      businessId: ['', [Validators.required]],
      propertyId: [''],
      customer: {},
      business: {},
      property: {},
      currency: 'GBP',
      quote_date: todaysDate.toISOString(),
      jobId: null
    })

    this.newQuote = new Quote().deserialize({});

    this.WorkItems = [];

    // Get selected Account currency
    this.authService.getSelectedAccount().then((account) => {
      this.accountCurrency = account.currency;

      // Patch New Invoice Form
      this.ionicForm.patchValue(
        {
          currency: account.currency
        }
      );

      // Currency for New Invoice Model
      this.newQuote.currency = account.currency
    });

    if (this.contactId) {
      this.setupContactForm(this.contactId);
    }

    // Get businesses and if we have 1 business, then pre-populate the form
    this.apiService.getBusinessList().subscribe(response => {
      if (response.length === 1) {
        this.ionicForm.patchValue(
          {
            businessId: response[0].id,
            business: {
              name: response[0].name
            }
          }
        );

        // Update model
        this.newQuote.businessId = response[0].id;

        // Chosen business
        this.chosenBusiness = response[0];
      }
    });
  }

  setupContactForm(contactId) {
    // Get contact
    this.apiService.getContact(contactId).subscribe(response => {

      // Pre-populate form values
      this.ionicForm.patchValue(
        {
          contactId: response.id,
          customer: {
            title: response.title,
            first_name: response.first_name,
            last_name: response.last_name,
            mainName: response.mainName,
            email: response.email,
            phone1: response.phone1,
            phone2: response.phone2,
            address1: response.address1,
            address2: response.address2,
            city: response.city,
            postcode: response.postcode
          }
        }
      );

      // Update model
      this.newQuote.contactId = response.id;

      // Fetch properties and pre-populate the property details from the contact
      this.apiService.getPropertyListByContact(response.id).subscribe(response => {

        // If there are no properties, then we should ensure that the form is reset for properties
        this.ionicForm.patchValue(
        {
          propertyId: '',
          property: {}
        });

        // Create a properties array for the picker with ID and address 1
        this.properties = response.map(function(p) {
          return {
            text: p.address1,
            value: p.id
          }
        });

      })
    })
  }

  dismiss() {  
    this.modalCtrl.dismiss();
  }

  async chooseCustomer() {
    const modal = await this.modalCtrl.create({  
      component: ContactsSelectorComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
      componentProps: {  },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {

      // Pre-populate form values
      this.ionicForm.patchValue(
        {
          contactId: data.id,
          customer: {
            title: data.title,
            first_name: data.first_name,
            last_name: data.last_name,
            mainName: data.mainName,
            email: data.email,
            phone1: data.phone1,
            phone2: data.phone2,
            address1: data.address1,
            address2: data.address2,
            city: data.city,
            postcode: data.postcode
          }
        }
      );

      // Update model
      this.newQuote.contactId = data.id;

      // Fetch properties and pre-populate the property details from the contact
      this.apiService.getPropertyListByContact(data.id).subscribe(response => {

        // If there are no properties, then we should ensure that the form is reset for properties
        this.ionicForm.patchValue(
        {
          propertyId: '',
          property: {}
        });

        // Create a properties array for the picker with ID and address 1
        this.properties = response.map(function(p) {
          return {
            text: p.address1,
            value: p.id
          }
        });

      })
    }
  }

  async openPropertyChooser() {
    const modal = await this.modalCtrl.create({  
      component: PropertyChooserComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
      componentProps: { contactId: this.newQuote.contactId },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {
      // Update this model
      this.newQuote.propertyId = data.id;

      // Patch the form with the property ID
      this.ionicForm.patchValue(
        {
          propertyId: data.id,
          property: {
            name: data.address1
          }
        }
      );

    }
  }

  async chooseBusiness() {
    const modal = await this.modalCtrl.create({  
      component: BusinessSelectorComponent,
      canDismiss: true,
      presentingElement: await this.modalCtrl.getTop(), // Get the top-most ion-modal
      componentProps: {  },
    });
   
    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (role === 'confirm') {

      this.ionicForm.patchValue(
        {
          businessId: data.id,
          business: {
            name: data.name
          }
        }
      );

      // Update model
      this.newQuote.businessId = data.id;

      // Chosen Business
      this.chosenBusiness = data;
    }
  }

  private prepareSave(): Quote {

    // Add invoice Date
    this.newQuote.quote_date = this.ionicForm.controls.quote_date.value

    return this.newQuote;
  }

  async submitForm() {
    this.isSubmitted = true;

    if (!this.ionicForm.valid) {
      console.log('Please provide all the required values for a quote.')
      return false;
    } else {

      // Prep the Invoice data
      const newQuote = this.prepareSave(); // `newQuote` is now an instance of "Quote"

      this.WorkItems = newQuote.WorkItems.slice(0);

      // TEMP: REMOVE FROM MODEL AS JSON SERVER SAVES IT ALL
      //delete newQuote.WorkItems;

      // API Call
      this.apiService.createQuote(newQuote).subscribe((response) => {

        // Update all Work Items with Invoice ID and Create Work Items
        const observables = this.WorkItems.map((item) => {
          // Set the invoiceId property
          item.quoteId = response.id;

          // Send to API and return the observable
          return this.apiService.createWorkItem(item);
        });

        forkJoin(observables).subscribe((responses) => {
          // This code will run after all the API calls are completed
          console.log('All API calls completed:', responses);

          // Execute your command here
          if (!this.jobId)
            this.router.navigateByUrl('/t/quotes/view/'+response.id);
        });

        // If no observables (no Work Items to save) then lets ensure we still route to the new invoice
        if (observables.length < 1) {
          if (!this.jobId)
            this.router.navigateByUrl('/t/quotes/view/'+response.id);
        }

        // Close the modal
        this.modalCtrl.dismiss(response); // TODO: Return data, although we should refresh previous view / form

      });

    }
  }

  get errorControl() {
    return this.ionicForm.controls;
  }

  get customerDisplay() {
    if (this.ionicForm.get('customer').value.mainName) {
      return this.ionicForm.get('customer').value.mainName+' - '+this.ionicForm.get('customer').value.email;
    }
    else {
      return 'Choose a customer';
    }
  }

  get businessDisplay() {
    if (this.ionicForm.get('business').value.name) {
      return this.ionicForm.get('business').value.name;
    }
    else {
      return 'Choose a Business';
    }
  }

  get propertyDisplay() {
    if (this.ionicForm.get('property').value.name) {
      return this.ionicForm.get('property').value.name;
    }
    else {
      return 'Choose a Property (optional)';
    }
  }

}
